import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, InputGroup } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { FiUpload, FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { fetchTeams } from "../../../redux/slices/teamSlice";
import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import TeamTable from "../../tables/TeamTable/TeamTable";
import ViewEditTeamModal from "../../Modal/ProjectModal/ViewEditTeamModal/ViewEditTeamModal";

const TeamsTab = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const reduxTeams = useSelector((state) => state.teams?.items || []);
    const delegations = useSelector((state) => state.delegations.items);

    const [subTab, setSubTab] = useState("lista");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTeam, setSelectedTeam] = useState(null);

    useEffect(() => {
        dispatch(fetchTeams());
    }, [dispatch]);

    const filteredTeams = useMemo(() => {
        const search = searchTerm.toLowerCase();
        const matches = reduxTeams.filter((team) => team.name?.toLowerCase().includes(search));
        return sortAlphabetically(matches, (team) => [team.name], i18n.language);
    }, [reduxTeams, searchTerm, i18n.language]);
    
    const handleOpenModal = (team) => setSelectedTeam(team);
    const handleCloseModal = () => setSelectedTeam(null);

    return (
        <div className="teams-tab-container">
            <div className="d-flex justify-content-between align-items-end mb-4 border-bottom">
                <div className="d-flex gap-4">
                    <button
                        className={`btn rounded-0 px-2 py-2 fw-semibold transition-all ${subTab === "lista" ? "txt-main" : "txt-subtle"
                            }`}
                        onClick={() => setSubTab("lista")}
                        style={{
                            boxShadow: "none",
                            border: "none",
                            backgroundColor: "transparent",
                            borderBottom:
                                subTab === "lista"
                                    ? "2px solid var(--text-primary)"
                                    : "2px solid transparent",
                        }}
                    >
                        {t("components.TeamsTab.tabs.list")}
                    </button>
                    <button
                        className={`btn rounded-0 px-2 py-2 fw-semibold transition-all ${subTab === "grafico" ? "txt-main" : "txt-subtle"
                            }`}
                        onClick={() => setSubTab("grafico")}
                        style={{
                            boxShadow: "none",
                            border: "none",
                            backgroundColor: "transparent",
                            borderBottom:
                                subTab === "grafico"
                                    ? "2px solid var(--text-primary)"
                                    : "2px solid transparent",
                        }}
                    >
                        {t("components.TeamsTab.tabs.chart")}
                    </button>
                </div>

                <div className="d-flex gap-2 pb-2">
                    <Button
                        variant="light"
                        className="bg-surface txt-subtle fw-semibold d-flex align-items-center gap-2 shadow-sm"
                        style={{ border: "var(--border-thin)", borderRadius: "20px" }}
                    >
                        <FiUpload size={16} /> {t("components.TeamsTab.buttons.importTeams")}
                    </Button>
                    <Button
                        variant="outline-danger"
                        className="fw-semibold d-flex align-items-center gap-2 shadow-sm"
                        style={{
                            color: "var(--brand-primary)",
                            borderColor: "var(--brand-primary)",
                            borderRadius: "20px",
                        }}
                    >
                        <FiPlus size={18} /> {t("components.TeamsTab.buttons.newTeam")}
                    </Button>
                </div>
            </div>

            <div className="atlar-card overflow-hidden">
                <div className="p-3 border-bottom bg-surface">
                    <InputGroup className="shadow-none">
                        <InputGroup.Text
                            className="bg-gray-50 border-end-0 border-0 rounded-start"
                            style={{ backgroundColor: "var(--gray-50)" }}
                        >
                            <FaSearch className="txt-subtle" />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={t("components.TeamsTab.searchPlaceholder")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-gray-50 border-start-0 border-0 shadow-none rounded-end txt-main"
                            style={{ backgroundColor: "var(--gray-50)" }}
                        />
                    </InputGroup>
                </div>

                <TeamTable 
                    teams={filteredTeams} 
                    delegations={delegations} 
                    onViewTeam={handleOpenModal} 
                />

                <div
                    className="p-3 border-top bg-gray-50 txt-subtle text-small"
                    style={{ backgroundColor: "var(--gray-50)" }}
                >
                    {t("components.TeamsTab.showingItems", { count: filteredTeams.length })}
                </div>
            </div>

            {selectedTeam && (
                <ViewEditTeamModal
                    team={selectedTeam}
                    onClose={handleCloseModal}
                    onUpdate={() => dispatch(fetchTeams())}
                />
            )}
        </div>
    );
};

export default TeamsTab;